import * as React from "react";
import { Chip } from "@mui/material";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";
import { resolveCurrentStep } from "./StatusTimeline";
import ViewDownloadButtons from "./ViewDownloadButtons";
import { C } from "../../theme/dashboardCardTokens";
import { useTranslation } from "../../i18n/LanguageContext";

// Admin/operator variant of the form chip - they aren't the ones filling
// out the patient's form, so clicking copies the link (for pasting into an
// email/support chat) instead of opening it.
function CopyFormLinkChip({ formUrl }) {
  const { t } = useTranslation();
  const [copied, setCopied] = React.useState(false);

  const handleClick = async () => {
    try {
      await navigator.clipboard.writeText(`${window.location.origin}${formUrl}`);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // clipboard API unavailable — nothing further we can do
    }
  };

  return (
    <Chip
      onClick={handleClick}
      clickable
      size="small"
      icon={
        copied ? (
          <CheckIcon sx={{ fontSize: 16, color: "#2E7D32" }} />
        ) : (
          <ContentCopyIcon sx={{ fontSize: 16, color: C.primary }} />
        )
      }
      label={copied ? t("portal.orderTestActionChips.copied") : t("portal.orderTestActionChips.copyFormLink")}
      sx={{
        bgcolor: copied ? "#e8f5e8" : C.tintBg,
        color: copied ? "#2E7D32" : C.primary,
        fontWeight: 600,
        borderRadius: "8px",
        border: `1px solid ${copied ? "#2E7D32" : C.primary}33`,
        pl: 1,
        pr: 1.25,
        "& .MuiChip-icon": { ml: 0.25 },
        "&:hover": { bgcolor: copied ? "#e8f5e8" : "#dce9f7" },
      }}
    />
  );
}

// Pill actions for one test, driven entirely by its resolved current step.
// `showFormChip` is false in the therapist context — therapists don't fill
// out patient forms even though form_submitted is present in the data.
// `formChipVariant` distinguishes the patient's "open the form" chip from
// the admin's "copy the link" chip - see CopyFormLinkChip above.
// `getReportLink(labTestId)` is fetched fresh on every click, never cached
// — it's a 5-minute presigned URL.
function OrderTestActionChips({ test, showFormChip, formChipVariant = "fill", getReportLink }) {
  const { t } = useTranslation();
  const currentStep = resolveCurrentStep(test.timeline);
  if (!currentStep) return null;

  if (
    showFormChip &&
    (currentStep.step_key === "dispatched" || currentStep.step_key === "received_by_customer")
  ) {
    if (formChipVariant === "copyLink") {
      return <CopyFormLinkChip formUrl={currentStep.form_url} />;
    }
    return (
      <Chip
        component="a"
        href={currentStep.form_url}
        clickable
        size="small"
        icon={<DescriptionOutlinedIcon sx={{ fontSize: 16, color: C.primary }} />}
        label={t("portal.orderTestActionChips.fillOutForm")}
        sx={{
          bgcolor: C.tintBg,
          color: C.primary,
          fontWeight: 600,
          borderRadius: "8px",
          border: `1px solid ${C.primary}33`,
          pl: 1,
          pr: 1.25,
          "& .MuiChip-icon": { ml: 0.25 },
          "&:hover": { bgcolor: "#dce9f7" },
        }}
      />
    );
  }

  if (currentStep.step_key === "completed") {
    if (currentStep.report_available) {
      return (
        <ViewDownloadButtons
          id={test.lab_test_id}
          filename={`Sibolab-Report-${test.test_identifier}.pdf`}
          getLink={getReportLink}
        />
      );
    }
    return (
      <Chip
        size="small"
        label={t("portal.orderTestActionChips.reportProcessing")}
        sx={{ bgcolor: "#f5f5f5", color: "#9E9E9E", fontWeight: 600, borderRadius: "8px", pl: 0.5, pr: 0.75 }}
      />
    );
  }

  return null;
}

export default OrderTestActionChips;
